import { removeUndefinedValues } from "../utils/firestore";
import {
  CollectionPaths,
  MenuGroupModifierGroupsStaleError,
  ValidationError,
  findMenuNameConflict,
} from "@order-up/common";
import {
  barPricesRefuseDirectItems,
  barPricesBlockDirectItemsMessage,
  buildDaysAvailableForWrite,
  IMenuGroup,
  IMenuGroupData,
  IMenuGroupDaysAvailable,
  ISubgroupReference,
  MenuGroup,
} from "@order-up/models";
import { MenuGroupSchema } from "@order-up/schemas";
import {
  DocumentWithId,
  FirestoreRepository,
  IBatchOperation,
} from "../interfaces/FirestoreRepository";
import { createLogger } from "../utils/logger";
import { counterService } from "./counters";
import { resolveLocationDocId, resolveTenantDocId } from "./resolveDocIds";

const logger = createLogger("MenuGroupService");

/**
 * Recompute the legacy projection from an authored `perDay`.
 *
 * This service is the CHOKE POINT for menu-group writes, and the projection is
 * service-owned: it is never trusted from the caller. That is what makes it
 * impossible to persist an authored schedule beside a stale set of day booleans
 * — a divergence no reader could detect, because both halves look valid on their
 * own. The schema refinement then verifies what this produced.
 *
 * Absent `perDay` ⇒ returned unchanged, so every existing document is untouched.
 */
function withRecomputedSchedule<T>(data: T): T {
  if (data == null || typeof data !== "object") return data;
  const source = data as Record<string, unknown>;
  const out: Record<string, unknown> = { ...source };
  let changed = false;

  const recompute = (value: unknown): IMenuGroupDaysAvailable | undefined => {
    const da = value as IMenuGroupDaysAvailable | undefined;
    if (da == null || da.perDay == null) return undefined;
    return buildDaysAvailableForWrite(da, da.perDay);
  };

  const topLevel = recompute(source.daysAvailable);
  if (topLevel != null) {
    out.daysAvailable = topLevel;
    changed = true;
  }

  // Per-parent overrides carry their own copy of the same shape.
  for (const key of ["subgroups", "modifierGroups"] as const) {
    const list = source[key];
    if (!Array.isArray(list)) continue;
    let listChanged = false;
    const next = list.map((entry) => {
      if (entry == null || typeof entry !== "object") return entry;
      const rebuilt = recompute((entry as Record<string, unknown>).daysAvailable);
      if (rebuilt == null) return entry;
      listChanged = true;
      return { ...(entry as Record<string, unknown>), daysAvailable: rebuilt };
    });
    if (listChanged) {
      out[key] = next;
      changed = true;
    }
  }

  return (changed ? out : data) as T;
}

/**
 * Utility function to remove undefined values from an object and convert model instances to plain objects
 * Firestore doesn't accept undefined values or custom class instances, so we need to exclude/convert them
 * Preserves Firestore server timestamps to maintain proper timestamp handling
 */
export /**
 * Optional menu-group fields an operator can legitimately set back to "none".
 *
 * Deliberately NOT every optional field. `groupFixedPrice` is excluded because
 * `MenuGroupModal` sends it as `undefined` on EVERY save (alongside
 * `groupPricingStrategy: "off"`), so treating that as intent would delete a
 * stored price each time any unrelated setting was edited.
 */
const CLEARABLE_FIELDS = [
  "courseId",
  "posName",
  "description",
  "image",
  "fixedPrice",
  "discountPercentage",
  "minRequired",
  "maxSelections",
  // Blank "Prep time (minutes)" = inherit from the parent group again. The
  // modal sends it on every save (value or undefined), so it is intent.
  "prepTimeMinutes",
] as const;

/**
 * Which fields this update is deliberately CLEARING, as `deleteField()` sentinels.
 *
 * A Firestore update cannot clear a field by omission, and `removeUndefinedValues`
 * strips every `undefined` — so "set the course back to none" wrote nothing at
 * all: no error, no console output, the old value simply retained. Setting a
 * value worked; unsetting one never did.
 *
 * The discriminator is presence, not value. A field the caller never touched is
 * ABSENT from `menuGroupData`; one the operator emptied is PRESENT and
 * `undefined`. Mirrors `resolveClearedCartFields` in `services/cart.ts`.
 */
export const resolveClearedMenuGroupFields = (
  menuGroupData: Partial<IMenuGroupData>,
): Record<string, unknown> => {
  const cleared: Record<string, unknown> = {};
  for (const field of CLEARABLE_FIELDS) {
    if (
      Object.prototype.hasOwnProperty.call(menuGroupData, field) &&
      (menuGroupData as Record<string, unknown>)[field] === undefined
    ) {
      cleared[field] = FirestoreRepository.deleteField();
    }
  }
  return cleared;
};

/**
 * The `modifierGroupsRev` a menu-group save writes (design 2026-09-30 B5).
 *
 * The menu editors save `modifierGroups[]` WHOLESALE, and the server writes
 * the same array — bar per-menu prices (`BarSpiritListService.saveMenuPrices`)
 * live on it. An editor opened before such a save would, on saving its older
 * copy, delete the prices with no error. So the array carries a revision:
 *
 *  - a save that does not write `modifierGroups` writes no revision;
 *  - a save that does must say which revision it loaded, as
 *    `modifierGroupsRev` (0 = the document had none). A loaded revision that
 *    is not the stored one is refused with
 *    `MenuGroupModifierGroupsStaleError`; otherwise the stored revision + 1 is
 *    written beside the array;
 *  - a caller that does not name a revision at all (the key is ABSENT — a
 *    present-but-undefined key means "loaded none" and reads as 0) built its
 *    array from this save's own fresh read, and is checked against that.
 *
 * The Firestore rules refuse a write that changes `modifierGroups` without
 * moving the revision by exactly one, so the check holds even when the read
 * here is stale or a client skips this service.
 */
export const planModifierGroupsRevision = (
  menuGroupData: Partial<IMenuGroup>,
  storedRev: number | undefined,
): { modifierGroupsRev?: number } => {
  if (menuGroupData.modifierGroups === undefined) return {};
  const stored = storedRev ?? 0;
  const loaded = Object.prototype.hasOwnProperty.call(menuGroupData, "modifierGroupsRev")
    ? (menuGroupData.modifierGroupsRev ?? 0)
    : stored;
  if (loaded !== stored) {
    throw new MenuGroupModifierGroupsStaleError({ loadedRev: loaded, storedRev: stored });
  }
  return { modifierGroupsRev: stored + 1 };
};

/**
 * B3 after the bar's save (design 2026-09-30): a group carrying a bar
 * price-only reference may not gain items of its own. The rule is
 * `barPricesRefuseDirectItems` in `@order-up/models` — the SAME function the
 * server import, bar-catalog adoption and the Mixed Drinks items apply.
 */
export const assertBarPricesAllowDirectItems = (
  menuGroupData: Partial<IMenuGroup>,
  existing: Pick<IMenuGroup, "name" | "items" | "modifierGroups">,
): void => {
  if (menuGroupData.items === undefined && menuGroupData.modifierGroups === undefined) return;
  const after = {
    items: menuGroupData.items ?? existing.items ?? [],
    modifierGroups: menuGroupData.modifierGroups ?? existing.modifierGroups ?? [],
  };
  if (!barPricesRefuseDirectItems(existing, after)) return;
  throw new ValidationError(barPricesBlockDirectItemsMessage(menuGroupData.name ?? existing.name), {
    code: "MENU_GROUP_BAR_PRICES_BLOCK_ITEMS",
  });
};

const isPermissionDenied = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as { code?: unknown }).code === "permission-denied";

/**
 * Gets all menu groups for a restaurant
 */
const getMenuGroupsByRestaurantId = async (
  tenantId: number,
  locationNumericId: number,
): Promise<MenuGroup[]> => {
  logger.debug(
    `Getting menu groups for tenantId: ${tenantId}, restaurantId: ${locationNumericId}`,
  );

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      console.warn(`[MenuGroupService] Tenant with ID ${tenantId} not found`);
      return [];
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      console.warn(
        `[MenuGroupService] Restaurant with ID ${locationNumericId} not found in tenant ${tenantId}`,
      );
      return [];
    }

    const collectionPath = CollectionPaths.menuGroups(
      tenantDocId,
      locationDocId,
    );
    const menuGroupRepo = new FirestoreRepository<MenuGroup>(
      collectionPath,
      MenuGroup,
    );
    const menuGroups = await menuGroupRepo.find();

    logger.debug(
      `Retrieved ${menuGroups.length} menu groups for restaurant ${locationNumericId}`,
    );
    return menuGroups;
  } catch (error) {
    logger.error(`Error fetching menu groups for restaurant ${locationNumericId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Gets a menu group by its business ID
 */
const getMenuGroupById = async (
  tenantId: number,
  locationNumericId: number,
  menuGroupId: number,
): Promise<MenuGroup | null> => {
  logger.debug(
    `Getting menu group ${menuGroupId} for restaurant ${locationNumericId}`,
  );

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) return null;

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) return null;

    const collectionPath = CollectionPaths.menuGroups(
      tenantDocId,
      locationDocId,
    );
    const menuGroupRepo = new FirestoreRepository<MenuGroup>(
      collectionPath,
      MenuGroup,
    );
    const results = await menuGroupRepo.find([
      { field: "menuGroupId", operator: "==", value: menuGroupId },
    ]);

    return results.length > 0 ? results[0] : null;
  } catch (error) {
    logger.error(`Error fetching menu group ${menuGroupId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Checks if a menu group name already exists (case-insensitive)
 */
const isNameTaken = async (
  tenantId: number,
  locationNumericId: number,
  name: string,
  excludeId?: number,
): Promise<{ taken: boolean; isArchived?: boolean }> => {
  const groups = await getMenuGroupsByRestaurantId(tenantId, locationNumericId);
  // The rule lives once in @order-up/common, shared with the server's
  // MenuEditService (POS menu editor) so the two planes cannot disagree.
  const conflict = findMenuNameConflict(
    groups,
    name,
    excludeId === undefined
      ? undefined
      : (group) => String(group.menuGroupId) === String(excludeId),
  );

  return {
    taken: !!conflict,
    isArchived: conflict ? conflict.isArchived : undefined,
  };
};

/**
 * Creates a new menu group
 */
const createMenuGroup = async (
  tenantId: number,
  locationNumericId: number,
  menuGroupData: IMenuGroup,
): Promise<DocumentWithId<MenuGroup>> => {
  logger.debug(`Creating menu group for restaurant ${locationNumericId}`, {
    menuGroupData: menuGroupData as unknown as Record<string, unknown>,
  });

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new Error(`Tenant with ID ${tenantId} not found`);
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new Error(`Restaurant with ID ${locationNumericId} not found`);
    }

    // Enterprise standard: Check for name uniqueness
    const conflict = await isNameTaken(
      tenantId,
      locationNumericId,
      menuGroupData.name,
    );
    if (conflict.taken) {
      const message = conflict.isArchived
        ? `A menu group with name "${menuGroupData.name}" already exists but is archived`
        : `A menu group with name "${menuGroupData.name}" already exists`;
      throw new Error(message);
    }

    // Use provided IDs if they exist, otherwise generate new ones
    let menuGroupId: number;
    let guid: string;

    if (menuGroupData.menuGroupId && menuGroupData.guid) {
      menuGroupId = menuGroupData.menuGroupId;
      guid = menuGroupData.guid;
    } else {
      const generated = await counterService.generateMenuGroupId(
        tenantId,
        locationNumericId,
      );
      menuGroupId = generated.menuGroupId;
      guid = generated.guid;
    }

    const {
      removedSubgroupIds,
      removedItemIds,
      removedModifierGroupIds,
      ...cleanMenuGroupData
    } = menuGroupData as any;
    const menuGroupForValidation = withRecomputedSchedule({
      ...cleanMenuGroupData,
      menuGroupId,
      guid,
    });

    MenuGroupSchema.parse(menuGroupForValidation);

    const menuGroupPayload: any = removeUndefinedValues({
      ...menuGroupForValidation,
      createdAt: FirestoreRepository.getServerTimestamp(),
      updatedAt: FirestoreRepository.getServerTimestamp(),
    });

    const collectionPath = CollectionPaths.menuGroups(
      tenantDocId,
      locationDocId,
    );
    const menuGroupRepo = new FirestoreRepository<MenuGroup>(
      collectionPath,
      MenuGroup,
    );
    const createdDoc = await menuGroupRepo.create(menuGroupPayload);

    return createdDoc;
  } catch (error) {
    logger.error(`Error creating menu group`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Creates multiple menu groups in a batch operation using Firestore writeBatch()
 */
const createMenuGroupsBatch = async (
  tenantId: number,
  locationNumericId: number,
  menuGroupsData: IMenuGroup[],
): Promise<DocumentWithId<MenuGroup>[]> => {
  logger.debug(
    `Creating ${menuGroupsData.length} menu groups in batch for restaurant ${locationNumericId}`,
  );

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new Error(`Tenant with ID ${tenantId} not found`);
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new Error(`Restaurant with ID ${locationNumericId} not found`);
    }

    const collectionPath = CollectionPaths.menuGroups(
      tenantDocId,
      locationDocId,
    );
    const menuGroupRepo = new FirestoreRepository<MenuGroup>(
      collectionPath,
      MenuGroup,
    );

    // Reserve ALL counter ids for rows that need them in ONE read-modify-write
    // (previously ids were generated one row at a time — ~5-6 serial Firestore
    // round trips per row). Rows that already carry both menuGroupId + guid keep
    // theirs and are not counted.
    const rowsNeedingIds = menuGroupsData.filter(
      (d) => !(d.menuGroupId && d.guid),
    ).length;
    const reservedIds =
      rowsNeedingIds > 0
        ? await counterService.generateMenuGroupIds(
            tenantId,
            locationNumericId,
            rowsNeedingIds,
          )
        : [];
    let reservedIndex = 0;

    const validatedPayloads: any[] = [];

    for (const menuGroupData of menuGroupsData) {
      let menuGroupId: number;
      let guid: string;

      if (menuGroupData.menuGroupId && menuGroupData.guid) {
        menuGroupId = menuGroupData.menuGroupId;
        guid = menuGroupData.guid;
      } else {
        const generated = reservedIds[reservedIndex++];
        menuGroupId = generated.menuGroupId;
        guid = generated.guid;
      }

      const {
        removedSubgroupIds,
        removedItemIds,
        removedModifierGroupIds,
        ...cleanMenuGroupData
      } = menuGroupData as any;
      const menuGroupForValidation = withRecomputedSchedule({
        ...cleanMenuGroupData,
        menuGroupId,
        guid,
      });

      MenuGroupSchema.parse(menuGroupForValidation);

      const menuGroupPayload: any = removeUndefinedValues(
        menuGroupForValidation,
      );
      validatedPayloads.push(menuGroupPayload);
    }

    const menuGroups = await menuGroupRepo.batchCreate(validatedPayloads);
    return menuGroups;
  } catch (error) {
    logger.error(`Error creating menu groups in batch`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Updates an existing menu group
 */
const updateMenuGroup = async (
  tenantId: number,
  locationNumericId: number,
  menuGroupId: number,
  menuGroupData: Partial<IMenuGroup>,
): Promise<DocumentWithId<MenuGroup>> => {
  logger.debug(
    `Updating menu group ${menuGroupId} for restaurant ${locationNumericId}`,
  );

  try {
    const existingGroup = await getMenuGroupById(
      tenantId,
      locationNumericId,
      menuGroupId,
    );
    if (!existingGroup) {
      throw new Error(`Menu group with ID ${menuGroupId} not found`);
    }

    if (
      menuGroupData.name &&
      menuGroupData.name.toLowerCase() !== existingGroup.name.toLowerCase()
    ) {
      const conflict = await isNameTaken(
        tenantId,
        locationNumericId,
        menuGroupData.name,
        menuGroupId,
      );
      if (conflict.taken) {
        const message = conflict.isArchived
          ? `A menu group with name "${menuGroupData.name}" already exists but is archived`
          : `A menu group with name "${menuGroupData.name}" already exists`;
        throw new Error(message);
      }
    }

    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) throw new Error(`Tenant with ID ${tenantId} not found`);

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );

    // Recompute BEFORE both the validation merge and the write payload below —
    // `normalizedUpdate` is built from this same object, and Firestore
    // `updateDoc` replaces a nested map wholesale, so validating one shape and
    // writing another would persist an unvalidated schedule.
    const scheduleAdjustedData = withRecomputedSchedule(menuGroupData);

    const mergedData = {
      ...existingGroup,
      ...scheduleAdjustedData,
    };

    const fieldsToRemove = [
      "docId",
      "removedSubgroupIds",
      "removedItemIds",
      "removedModifierGroupIds",
    ];
    const cleanedMergedData = { ...mergedData };
    for (const field of fieldsToRemove) {
      if (field in cleanedMergedData) {
        delete (cleanedMergedData as any)[field];
      }
    }

    MenuGroupSchema.parse(cleanedMergedData);

    // Throws before anything is written when the caller's copy of
    // `modifierGroups` is older than the stored one.
    const revision = planModifierGroupsRevision(
      menuGroupData,
      existingGroup.modifierGroupsRev,
    );
    assertBarPricesAllowDirectItems(menuGroupData, existingGroup);

    // Normalize parent fields: parentIds is canonical. If caller sends only parentId, set parentIds so Firestore stays consistent.
    const menuDataWithParent = menuGroupData as Partial<IMenuGroupData>;
    const normalizedUpdate: Record<string, unknown> = { ...scheduleAdjustedData };
    // The revision is never the caller's to write: only the planned one goes.
    delete normalizedUpdate.modifierGroupsRev;
    Object.assign(normalizedUpdate, revision);
    if (
      menuDataWithParent.parentId != null &&
      menuDataWithParent.parentId > 0 &&
      (menuDataWithParent.parentIds == null || menuDataWithParent.parentIds.length === 0)
    ) {
      normalizedUpdate.parentIds = [menuDataWithParent.parentId];
    }
    if (
      menuDataWithParent.parentIds != null &&
      menuDataWithParent.parentIds.length > 0 &&
      menuDataWithParent.parentId == null
    ) {
      normalizedUpdate.parentId = menuDataWithParent.parentIds[0];
    }

    const updatePayload: any = removeUndefinedValues({
      ...normalizedUpdate,
      updatedAt: FirestoreRepository.getServerTimestamp(),
    });

    // ── CLEARING AN OPTIONAL FIELD ──────────────────────────────────────────
    // `removeUndefinedValues` drops every `undefined`, which is right for a
    // field the caller never mentioned and WRONG for one it deliberately
    // cleared: Firestore then keeps the old value and the save looks like it
    // worked. Setting a course was possible; unsetting one was not, with no
    // error and nothing in the console.
    //
    // The two cases are distinguishable — an untouched field is ABSENT from
    // `menuGroupData`, a cleared one is PRESENT with the value `undefined` —
    // so `hasOwn` is the discriminator, and the clear becomes a real
    // `deleteField()`. Scoped to an allowlist so a caller sending a partial
    // update can never delete a field it simply had no opinion about.
    Object.assign(updatePayload, resolveClearedMenuGroupFields(menuGroupData));

    delete updatePayload.menuGroupId;
    delete updatePayload.guid;
    delete updatePayload.createdAt;

    const collectionPath = CollectionPaths.menuGroups(
      tenantDocId,
      locationDocId!,
    );
    const menuGroupRepo = new FirestoreRepository<MenuGroup>(
      collectionPath,
      MenuGroup,
    );

    try {
      await menuGroupRepo.updateById((existingGroup as any).docId, updatePayload);
    } catch (writeError) {
      // The rules refuse a `modifierGroups` write whose revision is not the
      // stored one + 1 — the server moved it between the read above and this
      // write. Say THAT, not "permission denied": re-read and compare.
      if (revision.modifierGroupsRev !== undefined && isPermissionDenied(writeError)) {
        const fresh = await menuGroupRepo.getById((existingGroup as any).docId);
        const freshRev = fresh?.modifierGroupsRev ?? 0;
        if (freshRev !== revision.modifierGroupsRev - 1) {
          throw new MenuGroupModifierGroupsStaleError(
            { loadedRev: revision.modifierGroupsRev - 1, storedRev: freshRev },
            { cause: writeError },
          );
        }
      }
      throw writeError;
    }

    const updatedDoc = await menuGroupRepo.getById(
      (existingGroup as any).docId,
    );
    if (!updatedDoc) {
      throw new Error(`Failed to fetch updated menu group ${menuGroupId}`);
    }

    return updatedDoc;
  } catch (error) {
    logger.error(`Error updating menu group`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Before a loop that saves SEVERAL menu groups' `modifierGroups` (the modifier
 * editors write a price or visibility override onto every menu that carries
 * the group), check every one of them is still at the revision the editor
 * loaded — and refuse the whole save, writing nothing, if ANY moved. Checking
 * inside the loop would save the first menus and refuse a later one, leaving a
 * half-applied edit the operator cannot see. `updateMenuGroup` still checks
 * each write, and the Firestore rules back both.
 */
const assertMenuGroupsFresh = async (
  tenantId: number,
  locationNumericId: number,
  loaded: ReadonlyArray<{ menuGroupId: number; modifierGroupsRev?: number; name?: string }>,
): Promise<void> => {
  const stale: Array<{ menuGroupId: number; name?: string; loadedRev: number; storedRev: number }> = [];
  for (const mg of loaded) {
    const fresh = await getMenuGroupById(tenantId, locationNumericId, mg.menuGroupId);
    if (!fresh) throw new Error(`Menu group with ID ${mg.menuGroupId} not found`);
    const loadedRev = mg.modifierGroupsRev ?? 0;
    const storedRev = fresh.modifierGroupsRev ?? 0;
    if (loadedRev !== storedRev) stale.push({ menuGroupId: mg.menuGroupId, name: fresh.name, loadedRev, storedRev });
  }
  if (stale.length > 0) throw new MenuGroupModifierGroupsStaleError({ stale });
};

/**
 * Deletes a menu group
 */
const deleteMenuGroup = async (
  tenantId: number,
  locationNumericId: number,
  menuGroupId: number,
): Promise<void> => {
  logger.debug(
    `Deleting menu group ${menuGroupId} for restaurant ${locationNumericId}`,
  );

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new Error(`Tenant with ID ${tenantId} not found`);
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new Error(`Restaurant with ID ${locationNumericId} not found`);
    }

    const collectionPath = CollectionPaths.menuGroups(
      tenantDocId,
      locationDocId,
    );
    const menuGroupRepo = new FirestoreRepository<MenuGroup>(
      collectionPath,
      MenuGroup,
    );

    // Find the menu group by its business ID
    const results = await menuGroupRepo.find([
      { field: "menuGroupId", operator: "==", value: menuGroupId },
    ]);
    if (results.length === 0) {
      throw new Error(`Menu group with ID ${menuGroupId} not found`);
    }

    await menuGroupRepo.deleteById(results[0].docId);
    logger.debug(`Successfully deleted menu group: ${menuGroupId}`);
  } catch (error) {
    logger.error(`Error deleting menu group`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Archives a menu group (soft delete)
 */
const archiveMenuGroup = async (
  tenantId: number,
  locationNumericId: number,
  menuGroupId: number,
): Promise<DocumentWithId<MenuGroup>> => {
  logger.debug(
    `Archiving menu group ${menuGroupId} for restaurant ${locationNumericId}`,
  );

  try {
    const existingGroup = await getMenuGroupById(
      tenantId,
      locationNumericId,
      menuGroupId,
    );
    if (!existingGroup) {
      throw new Error(`Menu group with ID ${menuGroupId} not found`);
    }

    const tenantDocId = await resolveTenantDocId(tenantId);
    const locationDocId = await resolveLocationDocId(
      tenantDocId!,
      locationNumericId,
    );

    const collectionPath = CollectionPaths.menuGroups(
      tenantDocId!,
      locationDocId!,
    );
    const menuGroupRepo = new FirestoreRepository<MenuGroup>(
      collectionPath,
      MenuGroup,
    );

    // Soft delete: set isArchived to true and archivedAt timestamp
    // Also set isActive to false for compatibility with components relying on it
    const archiveData = {
      isActive: false,
      isArchived: true,
      archivedAt: FirestoreRepository.getServerTimestamp(),
      updatedAt: FirestoreRepository.getServerTimestamp(),
    };

    await menuGroupRepo.updateById((existingGroup as any).docId, archiveData);

    // Fetch and return updated document
    const updatedDoc = await menuGroupRepo.getById(
      (existingGroup as any).docId,
    );
    if (!updatedDoc) {
      throw new Error(`Failed to fetch archived menu group ${menuGroupId}`);
    }

    logger.debug(`Successfully archived menu group ${menuGroupId}`);
    return updatedDoc;
  } catch (error) {
    logger.error(`Error archiving menu group ${menuGroupId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Restores an archived menu group
 */
const restoreMenuGroup = async (
  tenantId: number,
  locationNumericId: number,
  menuGroupId: number,
): Promise<DocumentWithId<MenuGroup>> => {
  logger.debug(
    `Restoring menu group ${menuGroupId} for restaurant ${locationNumericId}`,
  );

  try {
    const existingGroup = await getMenuGroupById(
      tenantId,
      locationNumericId,
      menuGroupId,
    );
    if (!existingGroup) {
      throw new Error(`Menu group with ID ${menuGroupId} not found`);
    }

    const tenantDocId = await resolveTenantDocId(tenantId);
    const locationDocId = await resolveLocationDocId(
      tenantDocId!,
      locationNumericId,
    );

    const collectionPath = CollectionPaths.menuGroups(
      tenantDocId!,
      locationDocId!,
    );
    const menuGroupRepo = new FirestoreRepository<MenuGroup>(
      collectionPath,
      MenuGroup,
    );

    // Restore: set isArchived to false, clear archivedAt, and set isActive to true
    const restoreData = {
      isActive: true,
      isArchived: false,
      archivedAt: FirestoreRepository.deleteField(),
      updatedAt: FirestoreRepository.getServerTimestamp(),
    };

    await menuGroupRepo.updateById((existingGroup as any).docId, restoreData);

    // Fetch and return updated document
    const updatedDoc = await menuGroupRepo.getById(
      (existingGroup as any).docId,
    );
    if (!updatedDoc) {
      throw new Error(`Failed to fetch restored menu group ${menuGroupId}`);
    }

    logger.debug(`Successfully restored menu group ${menuGroupId}`);
    return updatedDoc;
  } catch (error) {
    logger.error(`Error restoring menu group ${menuGroupId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

/**
 * Updates subgroup overrides on multiple parent groups atomically.
 *
 * Each parent stores a `subgroups[]` array of SubgroupReferences. This method
 * upserts the SubgroupReference for a given child sub-group in each parent's
 * array, using a Firestore batch write for atomicity.
 *
 * @param tenantId - Tenant business ID
 * @param locationNumericId - Restaurant business ID
 * @param subgroupId - The menuGroupId of the child sub-group being configured
 * @param overridesByParent - Map of parentMenuGroupId → override fields to set
 */
const updateSubgroupOverrides = async (
  tenantId: number,
  locationNumericId: number,
  subgroupId: number,
  overridesByParent: Record<number, Partial<ISubgroupReference>[]>,
): Promise<void> => {
  logger.debug(
    `Updating subgroup overrides for subgroup ${subgroupId} across ${Object.keys(overridesByParent).length} parents`,
  );

  try {
    const tenantDocId = await resolveTenantDocId(tenantId);
    if (!tenantDocId) {
      throw new Error(`Tenant with ID ${tenantId} not found`);
    }

    const locationDocId = await resolveLocationDocId(
      tenantDocId,
      locationNumericId,
    );
    if (!locationDocId) {
      throw new Error(`Restaurant with ID ${locationNumericId} not found`);
    }

    const collectionPath = CollectionPaths.menuGroups(
      tenantDocId,
      locationDocId,
    );
    const menuGroupRepo = new FirestoreRepository<MenuGroup>(
      collectionPath,
      MenuGroup,
    );

    // Fetch all parent groups that need updating
    const parentMenuGroupIds = Object.keys(overridesByParent).map(Number);
    const allGroups = await menuGroupRepo.find();

    // Build batch operations: for each parent, upsert the SubgroupReference
    const batchOps: IBatchOperation[] = [];

    for (const parentId of parentMenuGroupIds) {
      const parentGroup = allGroups.find(
        (g) => g.menuGroupId === parentId,
      );
      if (!parentGroup) {
        logger.warn(
          `Parent group ${parentId} not found — skipping subgroup override`,
        );
        continue;
      }

      const parentDocId = (parentGroup as any).docId as string;
      if (!parentDocId) {
        logger.warn(
          `Parent group ${parentId} has no docId — skipping subgroup override`,
        );
        continue;
      }

      // Get the array of override data for this parent (one per rootGroupId scope)
      const overrideDataArray = overridesByParent[parentId];

      // Build updated subgroups array, handling multiple entries per child (different rootGroupIds)
      const existingSubgroups: ISubgroupReference[] = parentGroup.subgroups ?? [];
      const updatedSubgroups = [...existingSubgroups];

      for (const overrideData of overrideDataArray) {
        const rootGroupId = overrideData.rootGroupId ?? undefined;

        // Find existing entry matching BOTH subgroupId AND rootGroupId
        const existingIndex = updatedSubgroups.findIndex(
          (s) =>
            s.subgroupId === subgroupId &&
            (s.rootGroupId ?? undefined) === rootGroupId,
        );

        // Merge: start from existing entry (if any), then apply incoming overrides.
        // This prevents fields not included in overrideData (e.g., daysAvailable)
        // from being silently dropped when only pricing is being updated.
        const existing = existingIndex >= 0 ? updatedSubgroups[existingIndex] : undefined;
        const subgroupRef: ISubgroupReference = {
          // Base fields (always set)
          subgroupId,
          position: overrideData.position ?? existing?.position ?? 0,
          ...(rootGroupId !== undefined && { rootGroupId }),
          // Visibility overrides (merge: override → existing → omit)
          ...(overrideData.isHidden !== undefined ? { isHidden: overrideData.isHidden } : existing?.isHidden !== undefined ? { isHidden: existing.isHidden } : {}),
          ...(overrideData.displayNameOverride !== undefined ? { displayNameOverride: overrideData.displayNameOverride } : existing?.displayNameOverride !== undefined ? { displayNameOverride: existing.displayNameOverride } : {}),
          ...(overrideData.visibleInPOS !== undefined ? { visibleInPOS: overrideData.visibleInPOS } : existing?.visibleInPOS !== undefined ? { visibleInPOS: existing.visibleInPOS } : {}),
          ...(overrideData.visibleInKiosk !== undefined ? { visibleInKiosk: overrideData.visibleInKiosk } : existing?.visibleInKiosk !== undefined ? { visibleInKiosk: existing.visibleInKiosk } : {}),
          ...(overrideData.visibleInOnlineOrdering !== undefined ? { visibleInOnlineOrdering: overrideData.visibleInOnlineOrdering } : existing?.visibleInOnlineOrdering !== undefined ? { visibleInOnlineOrdering: existing.visibleInOnlineOrdering } : {}),
          ...(overrideData.visibleToPartners !== undefined ? { visibleToPartners: overrideData.visibleToPartners } : existing?.visibleToPartners !== undefined ? { visibleToPartners: existing.visibleToPartners } : {}),
          ...(overrideData.visibleInInvoices !== undefined ? { visibleInInvoices: overrideData.visibleInInvoices } : existing?.visibleInInvoices !== undefined ? { visibleInInvoices: existing.visibleInInvoices } : {}),
          // Service-type availability (merge)
          ...(overrideData.availability !== undefined ? { availability: overrideData.availability } : existing?.availability !== undefined ? { availability: existing.availability } : {}),
          // Scheduling overrides (merge)
          // Same choke point as the group-level write: the projection is
          // service-owned on the override copy too, so a per-parent schedule
          // cannot be stored beside a stale set of day booleans.
          ...(overrideData.daysAvailable !== undefined
            ? withRecomputedSchedule({ daysAvailable: overrideData.daysAvailable })
            : existing?.daysAvailable !== undefined
              ? withRecomputedSchedule({ daysAvailable: existing.daysAvailable })
              : {}),
          ...(overrideData.dateRange !== undefined ? { dateRange: overrideData.dateRange } : existing?.dateRange !== undefined ? { dateRange: existing.dateRange } : {}),
          ...(overrideData.seasonalAvailability !== undefined ? { seasonalAvailability: overrideData.seasonalAvailability } : existing?.seasonalAvailability !== undefined ? { seasonalAvailability: existing.seasonalAvailability } : {}),
          // Pricing overrides (merge)
          ...(overrideData.groupPricingStrategyOverride !== undefined ? { groupPricingStrategyOverride: overrideData.groupPricingStrategyOverride } : existing?.groupPricingStrategyOverride !== undefined ? { groupPricingStrategyOverride: existing.groupPricingStrategyOverride } : {}),
          ...(overrideData.groupFixedPriceOverride !== undefined ? { groupFixedPriceOverride: overrideData.groupFixedPriceOverride } : existing?.groupFixedPriceOverride !== undefined ? { groupFixedPriceOverride: existing.groupFixedPriceOverride } : {}),
          ...(overrideData.groupSizePricesOverride !== undefined ? { groupSizePricesOverride: overrideData.groupSizePricesOverride } : existing?.groupSizePricesOverride !== undefined ? { groupSizePricesOverride: existing.groupSizePricesOverride } : {}),
        };

        // Replace or append in the subgroups array
        if (existingIndex >= 0) {
          updatedSubgroups[existingIndex] = subgroupRef;
        } else {
          updatedSubgroups.push(subgroupRef);
        }
      }

      // Clean the subgroups array for Firestore (remove undefined values)
      const cleanedSubgroups = updatedSubgroups.map((s) => removeUndefinedValues(s));

      batchOps.push({
        type: "update",
        collectionPath,
        documentId: parentDocId,
        data: {
          subgroups: cleanedSubgroups,
          updatedAt: new Date(),
        },
      });
    }

    if (batchOps.length === 0) {
      logger.warn("No valid parent groups found for subgroup override update");
      return;
    }

    // Execute all parent updates atomically
    await FirestoreRepository.executeBatch(batchOps);

    logger.info(
      `Successfully updated subgroup overrides for subgroup ${subgroupId} across ${batchOps.length} parents`,
    );
  } catch (error) {
    logger.error(`Error updating subgroup overrides for subgroup ${subgroupId}`, {
      error: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

export const menuGroupService = {
  getMenuGroupsByRestaurantId,
  getMenuGroupById,
  assertMenuGroupsFresh,
  createMenuGroup,
  createMenuGroupsBatch,
  updateMenuGroup,
  deleteMenuGroup,
  archiveMenuGroup,
  restoreMenuGroup,
  isNameTaken,
  updateSubgroupOverrides,
};
